// ─── Pure domain helpers for Reminder Rules ──────────────────────────────────
// No React, no side effects. Single source of truth for the rule enums, the
// blank form, payload assembly, and form validation.

/** When the reminder fires relative to the due date. */
export const DIRECTIONS = [
  { value: "before", label: "before due date" },
  { value: "on", label: "on due date" },
  { value: "after", label: "after due date" },
];

/** How often the reminder repeats. */
export const FREQUENCIES = [
  { value: "once", label: "Once" },
  { value: "daily", label: "Daily" },
  { value: "weekly", label: "Weekly" },
  { value: "custom", label: "Custom (recurring)…" },
];

/** Recurrence patterns, mirroring Outlook's recurrence dialog. */
export const RECURRENCE_PATTERNS = [
  { value: "daily", label: "Daily" },
  { value: "weekday", label: "Every weekday" },
  { value: "weekly", label: "Weekly" },
  { value: "monthly", label: "Monthly" },
  { value: "yearly", label: "Yearly" },
];

/** The unit each pattern repeats in, for the "every N …" control. */
export const RECURRENCE_UNITS = {
  daily: "day(s)",
  weekday: "",
  weekly: "week(s)",
  monthly: "month(s)",
  yearly: "year(s)",
};

/** Weekday choices, Sunday-first to match getUTCDay(). */
export const WEEKDAYS = [
  { value: 0, label: "Sun" },
  { value: 1, label: "Mon" },
  { value: 2, label: "Tue" },
  { value: 3, label: "Wed" },
  { value: 4, label: "Thu" },
  { value: 5, label: "Fri" },
  { value: 6, label: "Sat" },
];

/** Ordinal weeks for the "third Thursday" form. */
export const WEEK_NUMBERS = [
  { value: 1, label: "First" },
  { value: 2, label: "Second" },
  { value: 3, label: "Third" },
  { value: 4, label: "Fourth" },
  { value: -1, label: "Last" },
];

/** Months for a yearly pattern. */
export const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
].map((label, i) => ({ value: i + 1, label }));

/** How a recurring reminder stops. */
export const RECURRENCE_END_TYPES = [
  { value: "never", label: "No end date" },
  { value: "afterCount", label: "End after…" },
  { value: "byDate", label: "End by…" },
];

/** Blank recurrence settings, used when switching to a custom frequency. */
export const emptyRecurrence = () => ({
  pattern: "daily",
  interval: 1,
  weekdays: [],
  monthDay: "",
  weekNumber: "",
  dayOfWeek: "",
  month: "",
  endType: "never",
  endCount: 10,
  endDate: "",
});

/** Where recipients come from. */
export const RECIPIENT_MODES = [
  { value: "owner", label: "Owner (auto-resolved)" },
  { value: "explicit", label: "Specific emails" },
];

/** Email templates (each maps to a `<key>.html` on the server). */
export const TEMPLATES = [
  { value: "due-date-reminder", label: "Due date reminder" },
  { value: "data-owner-reminder", label: "Data owner reminder" },
  { value: "admin-reminder", label: "Admin reminder" },
  { value: "auditor-reminder", label: "Auditor reminder" },
];

/** Fallback timezones for browsers without `Intl.supportedValuesOf`. */
const FALLBACK_TIMEZONES = [
  "UTC",
  "Asia/Kolkata",
  "America/New_York",
  "America/Los_Angeles",
  "Europe/London",
  "Europe/Berlin",
  "Asia/Dubai",
  "Asia/Singapore",
  "Australia/Sydney",
];

/**
 * Every IANA timezone the browser knows (so a rule's send time can be set in any
 * zone), falling back to a short list on older browsers.
 */
export const TIMEZONES = (() => {
  try {
    if (typeof Intl.supportedValuesOf === "function") {
      return Intl.supportedValuesOf("timeZone");
    }
  } catch {
    return FALLBACK_TIMEZONES;
  }
  return FALLBACK_TIMEZONES;
})();

/**
 * The current UTC offset of a timezone as `UTC+05:30`, for the picker label.
 * @param {string} tz an IANA timezone.
 * @returns {string} the offset label, or "" if it can't be resolved.
 */
const timezoneOffset = (tz) => {
  try {
    const now = new Date();
    const parts = new Intl.DateTimeFormat("en-US", {
      timeZone: tz,
      hour12: false,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    })
      .formatToParts(now)
      .reduce((acc, part) => ({ ...acc, [part.type]: part.value }), {});
    const asUTC = Date.UTC(
      +parts.year,
      +parts.month - 1,
      +parts.day,
      +parts.hour % 24,
      +parts.minute,
      +parts.second,
    );
    const diffMinutes = Math.round((asUTC - now.getTime()) / 60000);
    const sign = diffMinutes >= 0 ? "+" : "-";
    const abs = Math.abs(diffMinutes);
    const hh = String(Math.floor(abs / 60)).padStart(2, "0");
    const mm = String(abs % 60).padStart(2, "0");
    return `UTC${sign}${hh}:${mm}`;
  } catch {
    return "";
  }
};

/**
 * Timezones as `{ value, label }`, the label carrying the UTC offset (e.g.
 * `(UTC+05:30) Asia/Kolkata`) so a user can pick by number as well as name.
 */
export const TIMEZONE_OPTIONS = TIMEZONES.map((tz) => {
  const offset = timezoneOffset(tz);
  return { value: tz, label: offset ? `(${offset}) ${tz}` : tz };
});

/**
 * The viewer's own timezone, used as a new rule's default so the picker starts
 * somewhere sensible.
 * @returns {string} an IANA timezone identifier.
 */
export const defaultTimezone = () => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "Asia/Kolkata";
  } catch {
    return "Asia/Kolkata";
  }
};

/**
 * A blank rule form.
 * @param {number|string} savedQueryId initial saved query id.
 * @param {string} dueDateField initial due-date field key.
 * @returns {object} the blank form state.
 */
export const emptyRuleForm = (savedQueryId = "", dueDateField = "") => ({
  name: "",
  savedQueryId,
  dueDateField,
  offsetDays: 7,
  direction: "before",
  frequency: "once",
  sendTime: "09:00",
  timezone: defaultTimezone(),
  recipientMode: "owner",
  explicitRecipients: "",
  templateKey: "due-date-reminder",
  isEnabled: true,
  recurrence: emptyRecurrence(),
});

/**
 * Turns a persisted rule back into form state, so an existing rule can be
 * edited in the same form that creates one.
 * @param {object} rule the rule as returned by the API.
 * @returns {object} the form state.
 */
export const ruleToForm = (rule) => ({
  name: rule.name || "",
  savedQueryId: String(rule.savedQueryId ?? ""),
  dueDateField: rule.dueDateField || "",
  offsetDays: rule.offsetDays ?? 7,
  direction: rule.direction || "before",
  frequency: rule.frequency || "once",
  sendTime: (rule.sendTime || "09:00").slice(0, 5),
  timezone: rule.timezone || defaultTimezone(),
  recipientMode: rule.recipientMode || "owner",
  explicitRecipients: (rule.explicitRecipients?.to || []).join(", "),
  templateKey: rule.templateKey || "due-date-reminder",
  isEnabled: !!rule.isEnabled,
  recurrence: {
    ...emptyRecurrence(),
    pattern: rule.recurrencePattern || "daily",
    interval: rule.recurrenceInterval ?? 1,
    weekdays: rule.recurrenceWeekdays
      ? String(rule.recurrenceWeekdays)
          .split(",")
          .map((d) => Number(d.trim()))
          .filter((d) => !Number.isNaN(d))
      : [],
    monthDay: rule.recurrenceMonthDay ?? "",
    weekNumber: rule.recurrenceWeekNumber ?? "",
    dayOfWeek: rule.recurrenceDayOfWeek ?? "",
    month: rule.recurrenceMonth ?? "",
    endType: rule.recurrenceEndType || "never",
    endCount: rule.recurrenceEndCount ?? 10,
    endDate: rule.recurrenceEndDate
      ? String(rule.recurrenceEndDate).slice(0, 10)
      : "",
  },
});

/** The two steps of rule creation: what to watch, then how to notify. */
export const RULE_STEPS = [
  { key: "target", label: "1. What to remind on" },
  { key: "delivery", label: "2. When and who" },
];

/** Splits a comma-separated recipient string into a trimmed, non-empty list. */
const parseEmails = (raw) =>
  String(raw)
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);

/**
 * Turns form state into the API payload the create/update endpoints expect.
 * @param {object} form the rule form state.
 * @returns {object} the API payload.
 */
export const buildRulePayload = (form) => {
  const payload = {
    name: form.name.trim(),
    savedQueryId: Number(form.savedQueryId),
    dueDateField: form.dueDateField,
    offsetDays: Number(form.offsetDays),
    direction: form.direction,
    frequency: form.frequency,
    recipientMode: form.recipientMode,
    templateKey: form.templateKey,
    isEnabled: !!form.isEnabled,
  };
  if (form.sendTime) payload.sendTime = form.sendTime;
  if (form.timezone) payload.timezone = form.timezone;
  if (form.recipientMode === "explicit") {
    payload.explicitRecipients = { to: parseEmails(form.explicitRecipients) };
  }
  if (form.frequency === "custom") {
    payload.recurrence = buildRecurrencePayload(form.recurrence);
  }
  return payload;
};

/**
 * Trims recurrence form state down to the fields the chosen pattern actually
 * uses, so an unused control left half-filled never reaches the API.
 * @param {object} recurrence the recurrence form state.
 * @returns {object} the recurrence payload.
 */
const buildRecurrencePayload = (recurrence = {}) => {
  const { pattern } = recurrence;
  const payload = { pattern, endType: recurrence.endType || "never" };

  if (pattern !== "weekday") {
    payload.interval = Number(recurrence.interval) || 1;
  }
  if (pattern === "weekly" && recurrence.weekdays?.length) {
    payload.weekdays = recurrence.weekdays.map(Number);
  }
  if (pattern === "monthly" || pattern === "yearly") {
    const ordinal =
      recurrence.weekNumber !== "" && recurrence.dayOfWeek !== "";
    if (ordinal) {
      payload.weekNumber = Number(recurrence.weekNumber);
      payload.dayOfWeek = Number(recurrence.dayOfWeek);
    } else if (recurrence.monthDay !== "") {
      payload.monthDay = Number(recurrence.monthDay);
    }
  }
  if (pattern === "yearly" && recurrence.month !== "") {
    payload.month = Number(recurrence.month);
  }
  if (payload.endType === "afterCount") {
    payload.endCount = Number(recurrence.endCount) || 1;
  }
  if (payload.endType === "byDate" && recurrence.endDate) {
    payload.endDate = recurrence.endDate;
  }
  return payload;
};

/**
 * Validates a rule form, returning the first problem or null when valid.
 * @param {object} form the rule form state.
 * @returns {string|null} an error message, or null.
 */
export const validateRuleForm = (form) => {
  if (!form.name.trim()) return "Name is required";
  if (!form.savedQueryId) return "Choose a saved query";
  if (!form.dueDateField) return "The selected query has no due date to remind on";
  if (Number(form.offsetDays) < 0) return "Offset days can't be negative";
  if (form.recipientMode === "explicit" && parseEmails(form.explicitRecipients).length === 0) {
    return "Add at least one recipient email";
  }
  if (form.frequency === "custom") return validateRecurrence(form.recurrence);
  return null;
};

/**
 * Validates recurrence settings for a custom frequency.
 * @param {object} recurrence the recurrence form state.
 * @returns {string|null} an error message, or null.
 */
const validateRecurrence = (recurrence = {}) => {
  const { pattern } = recurrence;
  if (!pattern) return "Choose a recurrence pattern";
  if (pattern !== "weekday" && Number(recurrence.interval) < 1) {
    return "Repeat interval must be at least 1";
  }
  if (pattern === "weekly" && !recurrence.weekdays?.length) {
    return "Pick at least one weekday";
  }
  if (
    (pattern === "monthly" || pattern === "yearly") &&
    recurrence.monthDay === "" &&
    (recurrence.weekNumber === "" || recurrence.dayOfWeek === "")
  ) {
    return "Choose a day of the month, or a week and weekday";
  }
  if (recurrence.endType === "afterCount" && Number(recurrence.endCount) < 1) {
    return "End after must be at least 1 occurrence";
  }
  if (recurrence.endType === "byDate" && !recurrence.endDate) {
    return "Choose an end date";
  }
  return null;
};

/**
 * Validates only the fields belonging to a step, so the user can't advance past
 * step one with it incomplete but isn't blocked by step two's fields yet.
 * @param {string} stepKey the step being left.
 * @param {object} form the rule form state.
 * @returns {string|null} an error message, or null.
 */
export const validateRuleStep = (stepKey, form) => {
  if (stepKey === "target") {
    if (!form.name.trim()) return "Name is required";
    if (!form.savedQueryId) return "Choose a saved query";
    if (!form.dueDateField) return "The selected query has no due date to remind on";
    return null;
  }
  return validateRuleForm(form);
};
